// The MCP servers the runner found in the user's Claude Code, Codex and Claude app that the board has not
// connected yet, and the one time the board tells the user about them. Nothing is connected from here: the
// user chooses each one in Connect.
import { useEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { MCP_SERVICES } from "../panels/connect/catalog";
import { set, toast, useStore } from "./store";
import { readStored, writeStored } from "../lib/stored";

/** The found servers with a web address that are not connected to this board yet. */
export function useFoundServers(): { name: string; url: string }[] {
  const mcps = useStore((s) => (s.runner?.online ? s.runner.mcps : undefined));
  const urls = useStore(useShallow((s) => Object.values(s.connections).map((c) => c.url)));
  return useMemo(() => (mcps?.remote ?? []).filter((s) => !urls.includes(s.url)), [mcps, urls]);
}

/** A list of names as a sentence says it: "A", "A and B", "A, B and 2 more". */
export function namesOf(list: { name: string; url: string }[]): string {
  const names = list.map((s) => MCP_SERVICES.find((x) => x.url === s.url)?.name ?? s.name);
  if (names.length > 3) return `${names.slice(0, 3).join(", ")} and ${names.length - 3} more`;
  return names.join(", ").replace(/, ([^,]*)$/, " and $1");
}

/**
 * Once for each computer: when it first links with servers the board could add, the user is told, with the way
 * to choose. Someone in Connect already sees them under the computer, so they are not told twice.
 */
export function useFoundServersPrompt() {
  const left = useFoundServers();
  const host = useStore((s) => s.runner?.host ?? null);
  const inConnect = useStore((s) => s.panel === "connect");
  useEffect(() => {
    if (!left.length) return;
    const key = `cb:found-told:${host ?? ""}`;
    if (readStored(key) || !writeStored(key, "1")) return;
    if (inConnect) return;
    toast({
      level: "info",
      title: left.length === 1 ? "An MCP server you use can be on your board" : `${left.length} MCP servers you use can be on your board`,
      body: `${namesOf(left)}, from your AI apps on ${host ?? "your computer"}. The board adds only the ones you choose.`,
      action: { label: "Choose", run: () => set({ panel: "connect", panelArg: { found: true } }) },
      ms: 12_000,
    });
  }, [left.length, host, inConnect]); // eslint-disable-line react-hooks/exhaustive-deps
}
